/**
 * Permission helpers for dynamic AuthZ (Permission → UI).
 * Do not gate UI on profile/role names — use these codes from /auth/me.
 */

export function normalizePermissionList(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return [...new Set(raw.map((p) => String(p || "").trim()).filter(Boolean))];
  }
  return [];
}

/** Flatten applications[].permissions from /auth/me payload (optionally one app). */
export function flattenPermissionsFromAuthMe(payload, appCode) {
  const apps = payload?.applications;
  if (!Array.isArray(apps)) {
    return normalizePermissionList(payload?.permissions);
  }
  const target = appCode
    ? String(appCode).toUpperCase().replace(/-$/, "")
    : null;
  const codes = [];
  apps.forEach((app) => {
    if (target && String(app?.code || "").toUpperCase() !== target) return;
    (app?.permissions || []).forEach((p) => codes.push(p));
  });
  return normalizePermissionList(codes);
}

/**
 * True when a profile object belongs to the given app code.
 */
function profileBelongsToApp(profile, app) {
  if (!profile || !app) return false;
  const apps = Array.isArray(profile.applications) ? profile.applications : [];
  if (apps.some((a) => String(a || "").toUpperCase() === app)) {
    return true;
  }
  const code = String(profile.code || "").toUpperCase();
  if (!code || code.includes(" + ")) return false;
  return code === app || code.startsWith(`${app}_`);
}

/**
 * Profile for the current app only (multi-app users have one profile per app).
 * Never returns another app's profile (e.g. HPOS_VIEWER while on HOMS).
 */
export function resolveProfileForApp(authzState, appType) {
  const app = normalizeAuthzAppType(appType);
  if (!app) return null;

  const profiles = Array.isArray(authzState?.profiles)
    ? authzState.profiles
    : [];
  if (profiles.length) {
    const match = profiles.find((p) => profileBelongsToApp(p, app));
    if (match) return match;
    // profiles[] present but none for this app → no entitlement
    return null;
  }

  const legacy = authzState?.profile;
  if (!legacy) return null;
  if (
    String(legacy.code || "").includes(" + ") ||
    String(legacy.name || "").includes(" + ")
  ) {
    return null;
  }
  return profileBelongsToApp(legacy, app) ? legacy : null;
}

export function resolveProfileLabelForApp(authzState, appType) {
  const profile = resolveProfileForApp(authzState, appType);
  if (!profile) return "";
  return profile.name || profile.code || "";
}

export function hasPermission(permissions, code) {
  if (!code) return true;
  const list = normalizePermissionList(permissions);
  if (!list.length) return false;
  return list.includes(String(code).trim());
}

export function hasAnyPermission(permissions, codes = []) {
  if (!codes || !codes.length) return true;
  return codes.some((code) => hasPermission(permissions, code));
}

export function hasAllPermissions(permissions, codes = []) {
  if (!codes || !codes.length) return true;
  return codes.every((code) => hasPermission(permissions, code));
}

export function hasApplicationAccess(applications, appCode) {
  if (!appCode) return true;
  const apps = Array.isArray(applications) ? applications : [];
  return apps.some(
    (a) => String(a?.code || "").toUpperCase() === String(appCode).toUpperCase()
  );
}

/** Permissions that unlock create/edit flows in HOMS when AuthZ is ready. */
export const HOMS_MUTATE_PERMISSIONS = [
  "sales.order.create",
  "sales.order.edit",
  "sales.quotation.create",
  "customer.create",
];

/** Settings / configuration access via UMS (not Keycloak Admin bucket). */
export const HOMS_SETTINGS_PERMISSIONS = [
  "settings.module.view",
  "settings.module.edit",
  "settings.data_wizard.run",
];

export function resolveHomsCanAccessSettings(authzState) {
  if (!isAuthzReady(authzState)) return false;
  return hasAnyPermission(authzState.permissions, HOMS_SETTINGS_PERMISSIONS);
}

/**
 * HOMS module page key → permission code (from HOMS_PAGE_DEFINITIONS + pipeline keys).
 * When AuthZ is ready, isPageOn checks these instead of coarse canMutate.
 */
export const HOMS_PAGE_PERMISSIONS = {
  "quotations.new": "sales.quotation.create",
  "quotations.edit": "sales.quotation.create",
  "quotations.detail": "sales.quotation.view",
  "sales_orders.create": "sales.order.create",
  "sales_orders.detail": "sales.order.view",
  "sales_orders.refund": "sales.order.edit",
  "deliveries.new": "sales.delivery.create",
  "deliveries.detail": "sales.delivery.view",
  "invoices.new": "sales.invoice.create",
  "invoices.detail": "sales.invoice.view",
  "invoices.opening_balance": "sales.opening_balance.create",
  "invoices.payment": "sales.invoice.payment",
  "invoices.send": "sales.invoice.edit",
  "customers.add": "customer.create",
  "customers.edit": "customer.create",
  "products.create": "product.create",
  "products.edit": "product.edit",
  "categories.add": "product.create",
  "inventory.history": "inventory.stock.view",
  "inventory.adjust": "inventory.stock.adjust",
};

/** HOMS field-group key → permission (L3 gates, e.g. inventory list actions). */
export const HOMS_FIELD_GROUP_PERMISSIONS = {
  "inventory.list_actions": "inventory.stock.adjust",
};

export function permissionForHomsPageKey(pageKey) {
  if (!pageKey) return null;
  return HOMS_PAGE_PERMISSIONS[String(pageKey).trim()] || null;
}

export function permissionForHomsFieldGroup(groupKey) {
  if (!groupKey) return null;
  return HOMS_FIELD_GROUP_PERMISSIONS[String(groupKey).trim()] || null;
}

/**
 * Permission → UI for a HOMS page key when AuthZ is loaded.
 * Returns null when AuthZ is not ready (caller keeps role/canMutate fallback).
 */
export function resolveHomsPagePermissionAccess(authzState, pageKey, canMutateFallback) {
  if (!isAuthzReady(authzState)) return null;

  const permissions = authzState.permissions || [];
  const required = permissionForHomsPageKey(pageKey);
  if (required) {
    return hasPermission(permissions, required);
  }

  const key = String(pageKey || "").toLowerCase();
  const isMutating =
    key.endsWith(".create") ||
    key.endsWith(".edit") ||
    key.endsWith(".add") ||
    key.endsWith(".new") ||
    key.includes(".create.") ||
    key.includes(".edit.");

  if (isMutating) {
    return Boolean(canMutateFallback);
  }

  return true;
}

export function resolveHomsFieldGroupPermissionAccess(
  authzState,
  groupKey,
  canMutateFallback
) {
  if (!isAuthzReady(authzState)) return null;

  const permissions = authzState.permissions || [];
  const required = permissionForHomsFieldGroup(groupKey);
  if (required) {
    return hasPermission(permissions, required);
  }

  return Boolean(canMutateFallback);
}

export function isAuthzReady(authzState) {
  const permissions = authzState?.permissions || [];
  return authzState?.status === "ready" && permissions.length > 0;
}

/**
 * True when UMS permissions should drive menus/routes (ready or stale-while-revalidate).
 * False on error → fall back to Keycloak role buckets.
 */
export function shouldUsePermissionUi(appType, authzState) {
  const app = normalizeAuthzAppType(appType);
  if (!app) return false;
  if (authzState?.status === "error") return false;

  const permissions = authzState?.permissions || [];
  if (!permissions.length) return false;

  if (authzState?.status === "ready") return true;
  // Tab refocus: keep last /auth/me permissions while reloading
  if (authzState?.status === "loading") return true;

  return false;
}

/**
 * First /auth/me in flight — hold UI (no Keycloak role flash).
 */
export function isAuthzBootstrapPending(appType, authzState) {
  const app = normalizeAuthzAppType(appType);
  if (!app) return false;

  const status = authzState?.status || "idle";
  const permissions = authzState?.permissions || [];

  if (status === "error" || status === "ready") return false;
  if (status === "loading" && permissions.length > 0) return false;

  return status === "idle" || status === "loading";
}

/**
 * User has UMS entitlement for this app (profile and/or app entry in /auth/me).
 * Scoped permissions alone are not enough without an app match.
 */
export function hasAuthzAppAccess(appType, authzState) {
  const app = normalizeAuthzAppType(appType);
  if (!app) return true;

  if (resolveProfileForApp(authzState, app)) return true;

  const applications = Array.isArray(authzState?.applications)
    ? authzState.applications
    : [];
  return applications.some(
    (a) => String(a?.code || "").toUpperCase() === app
  );
}

/**
 * Fail closed: /auth/me finished (or failed) and this app has no UMS profile.
 * Do NOT fall back to Keycloak Viewer menus for AuthZ apps.
 */
export function isAuthzAppAccessDenied(appType, authzState) {
  if (!isAuthzAppType(appType)) return false;
  const status = authzState?.status || "idle";
  if (status === "error") return true;
  if (status === "ready") return !hasAuthzAppAccess(appType, authzState);
  return false;
}

/**
 * Permission → UI: when /auth/me is loaded, ignore Keycloak Viewer for mutate.
 * Falls back to role buckets until AuthZ is available.
 */
export function resolveHomsCanMutate(authzState, userRole, roleCanMutateFn) {
  if (isAuthzReady(authzState)) {
    return hasAnyPermission(authzState.permissions, HOMS_MUTATE_PERMISSIONS);
  }
  return typeof roleCanMutateFn === "function" ? roleCanMutateFn(userRole) : false;
}

/**
 * HOMS menu path → required permission (view-level).
 * Missing mapping → allow (legacy role menus still apply until mapped).
 */
export const HOMS_PATH_PERMISSIONS = {
  "/reports/dashboard": "sales.report.view",
  "/reports/sales/overview": "sales.report.view",
  "/reports/sales/by-product": "sales.report.view",
  "/reports/sales/top-customers": "sales.report.view",
  "/SalesPipeline": "sales.order.view",
  "/Quotations": "sales.quotation.view",
  "/Sales Order": "sales.order.view",
  "/ViewOrders": "sales.order.view",
  "/Deliveries": "sales.delivery.view",
  "/Invoices": "sales.invoice.view",
  "/Customers": "customer.view",
  "/Products": "product.view",
  "/Categories": "product.view",
  "/inventory": "inventory.stock.view",
  "/inventory/reorder-alerts": "inventory.stock.view",
  "/zatca/invoices": "sales.invoice.view",
  "/configuration/data-wizard": "settings.data_wizard.run",
  "/configuration/business-settings": "settings.module.edit",
  "/configuration/tax-configuration": "settings.module.edit",
  "/configuration/printer-settings": "settings.module.edit",
  "/configuration/module-settings": "settings.module.view",
};

export function permissionForHomsPath(path) {
  if (!path) return null;
  if (HOMS_PATH_PERMISSIONS[path]) return HOMS_PATH_PERMISSIONS[path];
  const match = Object.keys(HOMS_PATH_PERMISSIONS)
    .filter((p) => path === p || path.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];
  return match ? HOMS_PATH_PERMISSIONS[match] : null;
}

/** Filter sidebar sections when AuthZ is ready. Logout/actions always kept. */
export function filterMenuSectionsByPermissions(sections, permissions) {
  return filterMenuSectionsByAppPermissions(sections, permissions, "HOMS");
}

/** Inject SETTINGS section for UMS profiles with settings.* (Keycloak Viewer menu omits it). */
export function appendHomsSettingsMenuSection(
  sections,
  permissions,
  menuItems = [],
  isPathEnabled
) {
  return appendAppSettingsMenuSection(sections, permissions, menuItems, "HOMS", isPathEnabled);
}

// ---------------------------------------------------------------------------
// Multi-app AuthZ (HPOS, HPRMS, ACCOUNTS)
// ---------------------------------------------------------------------------

export function normalizeAuthzAppType(appType) {
  const clean = String(appType || "").replace(/-$/, "").toUpperCase();
  if (["HOMS", "HPRMS", "HPOS", "ACCOUNTS"].includes(clean)) {
    return clean;
  }
  return null;
}

export function isAuthzAppType(appType) {
  return normalizeAuthzAppType(appType) != null;
}

export const SETTINGS_PERMISSIONS = HOMS_SETTINGS_PERMISSIONS;

export const HPRMS_MUTATE_PERMISSIONS = [
  "purchase.request.create",
  "purchase.rfq.create",
  "purchase.order.create",
  "purchase.vendor.create",
];

export const HPOS_MUTATE_PERMISSIONS = [
  "pos.shift.open",
  "pos.sale.create",
  "customer.create",
];

export const ACCOUNTS_MUTATE_PERMISSIONS = [
  "accounts.journal.create",
  "accounts.chart.edit",
];

const APP_MUTATE_PERMISSIONS = {
  HOMS: HOMS_MUTATE_PERMISSIONS,
  HPRMS: HPRMS_MUTATE_PERMISSIONS,
  HPOS: HPOS_MUTATE_PERMISSIONS,
  ACCOUNTS: ACCOUNTS_MUTATE_PERMISSIONS,
};

export const HPRMS_PAGE_PERMISSIONS = {
  "purchase_requests.new": "purchase.request.create",
  "purchase_requests.edit": "purchase.request.edit",
  "purchase_requests.detail": "purchase.request.view",
  "rfq.new": "purchase.rfq.create",
  "rfq.edit": "purchase.rfq.edit",
  "rfq.detail": "purchase.rfq.view",
  "rfq.compare": "purchase.rfq.view",
  "vendor_quotations.new": "purchase.rfq.create",
  "vendor_quotations.detail": "purchase.rfq.view",
  "purchase_orders.create": "purchase.order.create",
  "purchase_orders.detail": "purchase.order.view",
  "vendor_bills.new": "purchase.bill.create",
  "vendor_bills.detail": "purchase.bill.view",
  "vendor_payments.new": "purchase.payment.create",
  "vendor_payments.detail": "purchase.payment.view",
  "vendors.add": "purchase.vendor.create",
  "products.create": "product.create",
  "products.edit": "product.edit",
  "categories.add": "product.create",
  "inventory.history": "inventory.stock.view",
  "goods_receipts.new": "purchase.receipt.create",
  "goods_receipts.detail": "purchase.receipt.view",
  "quality_inspections.new": "purchase.inspection.create",
  "quality_inspections.detail": "purchase.inspection.view",
  "purchase_returns.new": "purchase.return.create",
  "purchase_returns.detail": "purchase.return.view",
};

export const HPRMS_FIELD_GROUP_PERMISSIONS = {
  "inventory.list_actions": "inventory.stock.adjust",
};

export const HPRMS_PATH_PERMISSIONS = {
  "/reports/dashboard": "purchase.report.view",
  "/reports/purchases/overview": "purchase.report.view",
  "/reports/inventory/overview": "purchase.report.view",
  "/PurchaseRequests": "purchase.request.view",
  "/PurchaseRequest": "purchase.request.view",
  "/RFQs": "purchase.rfq.view",
  "/RFQ": "purchase.rfq.view",
  "/VendorQuotations": "purchase.rfq.view",
  "/VendorQuotation": "purchase.rfq.view",
  "/Purchase Orders": "purchase.order.view",
  "/PurchaseOrder": "purchase.order.view",
  "/ViewPurchaseOrders": "purchase.order.view",
  "/VendorBills": "purchase.bill.view",
  "/VendorBill": "purchase.bill.view",
  "/VendorPayments": "purchase.payment.view",
  "/Vendors": "purchase.vendor.view",
  "/addVendor": "purchase.vendor.create",
  "/editVendor": "purchase.vendor.view",
  "/Vendor": "purchase.vendor.view",
  "/Products": "product.view",
  "/ProductForm": "product.create",
  "/editproduct": "product.edit",
  "/Categories": "product.view",
  "/Category": "product.create",
  "/inventory": "inventory.stock.view",
  "/inventory/reorder-alerts": "inventory.stock.view",
  "/inventoryHistory": "inventory.stock.view",
  "/inventryHistory": "inventory.stock.view",
  "/GoodsReceipts": "purchase.receipt.view",
  "/GoodsReceipt": "purchase.receipt.view",
  "/QualityInspections": "purchase.inspection.view",
  "/QualityInspection": "purchase.inspection.view",
  "/PurchaseReturns": "purchase.return.view",
  "/PurchaseReturn": "purchase.return.view",
  "/grn": "purchase.receipt.view",
  "/configuration/data-wizard": "settings.data_wizard.run",
  "/configuration/module-settings": "settings.module.view",
  "/configuration/tax-configuration": "settings.module.edit",
};

export const HPOS_PATH_PERMISSIONS = {
  "/Pos": "pos.sale.create",
  "/POS": "pos.sale.create",
  "/Pos/sales": "pos.sale.view",
  "/POS/sales": "pos.sale.view",
  "/Pos/shift/open": "pos.shift.open",
  "/POS/shift/open": "pos.shift.open",
  "/Pos/shift/close": "pos.shift.close",
  "/POS/shift/close": "pos.shift.close",
  "/checkout": "pos.sale.create",
  "/Customers": "customer.view",
  "/customer": "customer.create",
  "/editcustomer": "customer.create",
  "/Products": "product.view",
  "/ProductForm": "product.create",
  "/editproduct": "product.edit",
  "/Categories": "product.view",
  "/Category": "product.create",
  "/inventory": "inventory.stock.view",
  "/inventory/reorder-alerts": "inventory.stock.view",
  "/inventoryHistory": "inventory.stock.view",
  "/inventryHistory": "inventory.stock.view",
  "/refund": "pos.sale.refund",
  "/configuration/data-wizard": "settings.data_wizard.run",
  "/configuration/tax-configuration": "settings.module.edit",
};

const AUTHZ_FALLBACK_PATH_ORDER = {
  HPOS: ["/POS", "/POS/sales", "/Customers", "/Products", "/Categories", "/inventory"],
  HOMS: [
    "/reports/dashboard",
    "/SalesPipeline",
    "/Quotations",
    "/Customers",
    "/Products",
    "/inventory",
  ],
  HPRMS: [
    "/reports/dashboard",
    "/PurchaseRequests",
    "/Products",
    "/inventory",
  ],
  ACCOUNTS: ["/accounts/chart", "/accounts/journal", "/accounts/reports"],
};

export const ACCOUNTS_PATH_PERMISSIONS = {
  "/accounts/chart": "accounts.chart.view",
  "/accounts/journal": "accounts.journal.view",
  "/accounts/reports": "accounts.report.view",
  "/configuration/module-settings": "settings.module.view",
};

const APP_PATH_PERMISSIONS = {
  HOMS: HOMS_PATH_PERMISSIONS,
  HPRMS: HPRMS_PATH_PERMISSIONS,
  HPOS: HPOS_PATH_PERMISSIONS,
  ACCOUNTS: ACCOUNTS_PATH_PERMISSIONS,
};

const APP_PAGE_PERMISSIONS = {
  HOMS: HOMS_PAGE_PERMISSIONS,
  HPRMS: HPRMS_PAGE_PERMISSIONS,
};

const APP_FIELD_GROUP_PERMISSIONS = {
  HOMS: HOMS_FIELD_GROUP_PERMISSIONS,
  HPRMS: HPRMS_FIELD_GROUP_PERMISSIONS,
};

export function permissionForAppPath(appType, path) {
  const app = normalizeAuthzAppType(appType);
  if (!app || !path) return null;
  const map = APP_PATH_PERMISSIONS[app] || {};
  if (map[path]) return map[path];
  const match = Object.keys(map)
    .filter((p) => path === p || path.startsWith(`${p}/`))
    .sort((a, b) => b.length - a.length)[0];
  return match ? map[match] : null;
}

export function permissionForAppPageKey(appType, pageKey) {
  const app = normalizeAuthzAppType(appType);
  if (!app || !pageKey) return null;
  const map = APP_PAGE_PERMISSIONS[app] || {};
  return map[String(pageKey).trim()] || null;
}

export function permissionForAppFieldGroup(appType, groupKey) {
  const app = normalizeAuthzAppType(appType);
  if (!app || !groupKey) return null;
  const map = APP_FIELD_GROUP_PERMISSIONS[app] || {};
  return map[String(groupKey).trim()] || null;
}

export function resolveAppCanMutate(appType, authzState, userRole, roleCanMutateFn) {
  const app = normalizeAuthzAppType(appType);
  if (app === "HOMS") {
    return resolveHomsCanMutate(authzState, userRole, roleCanMutateFn);
  }
  const mutateCodes = app ? APP_MUTATE_PERMISSIONS[app] : null;
  if (isAuthzReady(authzState) && mutateCodes) {
    return hasAnyPermission(authzState.permissions, mutateCodes);
  }
  return typeof roleCanMutateFn === "function" ? roleCanMutateFn(userRole) : false;
}

export function resolveAppCanAccessSettings(appType, authzState) {
  if (!shouldUsePermissionUi(appType, authzState)) return false;
  return hasAnyPermission(authzState.permissions, SETTINGS_PERMISSIONS);
}

export function resolveAppPagePermissionAccess(
  appType,
  authzState,
  pageKey,
  canMutateFallback
) {
  if (!isAuthzReady(authzState)) return null;

  const permissions = authzState.permissions || [];
  const required = permissionForAppPageKey(appType, pageKey);
  if (required) {
    return hasPermission(permissions, required);
  }

  const key = String(pageKey || "").toLowerCase();
  const isMutating =
    key.endsWith(".create") ||
    key.endsWith(".edit") ||
    key.endsWith(".add") ||
    key.endsWith(".new") ||
    key.includes(".create.") ||
    key.includes(".edit.");

  if (isMutating) {
    return Boolean(canMutateFallback);
  }

  return true;
}

export function resolveAppFieldGroupPermissionAccess(
  appType,
  authzState,
  groupKey,
  canMutateFallback
) {
  if (!isAuthzReady(authzState)) return null;

  const permissions = authzState.permissions || [];
  const required = permissionForAppFieldGroup(appType, groupKey);
  if (required) {
    return hasPermission(permissions, required);
  }

  return Boolean(canMutateFallback);
}

export function filterMenuSectionsByAppPermissions(sections, permissions, appType) {
  const list = normalizePermissionList(permissions);
  if (!list.length) return sections;

  return (sections || [])
    .map((section) => ({
      ...section,
      items: (section.items || []).filter((item) => {
        if (item.action) return true;
        const required = permissionForAppPath(appType, item.path);
        if (!required) return true;
        return hasPermission(list, required);
      }),
    }))
    .filter((section) => (section.items || []).length > 0);
}

export function appendAppSettingsMenuSection(
  sections,
  permissions,
  menuItems = [],
  appType,
  isPathEnabled
) {
  const list = normalizePermissionList(permissions);
  if (!list.length || !list.some((p) => String(p).startsWith("settings."))) {
    return sections;
  }
  if ((sections || []).some((s) => s.section === "SETTINGS")) {
    return sections;
  }

  const settingsItems = (menuItems || []).filter((item) => {
    const required = permissionForAppPath(appType, item.path);
    if (required && !hasPermission(list, required)) return false;
    if (typeof isPathEnabled === "function" && !isPathEnabled(item.path)) return false;
    return true;
  });

  if (!settingsItems.length) return sections;

  const settingsSection = { section: "SETTINGS", items: settingsItems };
  const accountIdx = (sections || []).findIndex((s) => s.section === "ACCOUNT");
  if (accountIdx >= 0) {
    return [
      ...(sections || []).slice(0, accountIdx),
      settingsSection,
      ...(sections || []).slice(accountIdx),
    ];
  }
  return [...(sections || []), settingsSection];
}

/** UMS industrial admin — full app menu (not Keycloak Admin bucket). */
export function isAuthzAppAdmin(appType, authzState) {
  if (!shouldUsePermissionUi(appType, authzState)) return false;
  const app = normalizeAuthzAppType(appType);
  if (!app) return false;

  const profileCode = String(
    resolveProfileForApp(authzState, app)?.code || ""
  ).toUpperCase();
  const perms = authzState.permissions || [];

  if (app === "HOMS" && profileCode === "HOMS_ADMIN") return true;
  if (app === "HPRMS" && profileCode === "HPRMS_ADMIN") return true;
  if (app === "HPOS" && profileCode === "HPOS_ADMIN") return true;
  if (app === "ACCOUNTS" && profileCode === "ACCOUNTS_ADMIN") return true;

  return hasPermission(perms, "settings.module.edit");
}

/**
 * Upgrade Keycloak Viewer/Agent menu bucket when UMS profile grants broader access.
 * e.g. HPRMS_ADMIN on Viewer → admin menu (Products, Inventory, Settings).
 */
export function resolveAuthzMenuBucket(appType, uiMenuKey, authzState) {
  if (!isAuthzReady(authzState)) return uiMenuKey;
  if (isAuthzAppAdmin(appType, authzState)) return "admin";

  const app = normalizeAuthzAppType(appType);
  const profileCode = String(
    resolveProfileForApp(authzState, app)?.code || ""
  ).toUpperCase();

  if (app === "HPRMS") {
    if (profileCode === "HPRMS_MANAGER" || profileCode === "HPRMS_BUYER") return "manager";
  }
  if (app === "HOMS") {
    if (profileCode === "HOMS_MANAGER") return "manager";
    if (profileCode === "SALES_AGENT") return "agent";
  }
  if (app === "HPOS") {
    if (profileCode === "HPOS_MANAGER") return "manager";
    if (profileCode === "HPOS_CASHIER") return "agent";
  }

  return uiMenuKey;
}

/** First navigable path the user may access (for redirects off denied routes). */
export function resolveAuthzFallbackPath(appType, permissions) {
  const app = normalizeAuthzAppType(appType);
  if (!app) return null;

  const list = normalizePermissionList(permissions);
  const map = APP_PATH_PERMISSIONS[app] || {};
  const preferred = AUTHZ_FALLBACK_PATH_ORDER[app] || [];

  for (let i = 0; i < preferred.length; i += 1) {
    const path = preferred[i];
    const required = map[path];
    if (required && hasPermission(list, required)) return path;
  }

  const paths = Object.keys(map).sort((a, b) => a.length - b.length);
  for (let i = 0; i < paths.length; i += 1) {
    const path = paths[i];
    const required = map[path];
    if (required && hasPermission(list, required)) return path;
  }

  return null;
}

/** True when AuthZ is ready and the path is allowed (unmapped paths pass through). */
export function canAccessAppPath(appType, pathname, authzState) {
  if (!shouldUsePermissionUi(appType, authzState)) return true;
  if (isAuthzAppAdmin(appType, authzState)) return true;

  const app = normalizeAuthzAppType(appType);
  if (!app) return true;

  const required = permissionForAppPath(app, pathname);
  if (!required) return true;

  return hasPermission(authzState.permissions, required);
}
